import { useState, useEffect, useCallback } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { getUnresolvedConflicts } from '@/lib/syncQueue';
import ConflictResolutionModal from './ConflictResolutionModal';

export default function OfflineStatusBar() {
  const { isOnline, isSyncing, syncStatus, pendingChanges, lastSyncTime, triggerSync } = useOnlineStatus();
  const [conflictCount, setConflictCount] = useState(0);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const refreshConflictCount = useCallback(() => {
    setConflictCount(getUnresolvedConflicts().length);
  }, []);

  useEffect(() => {
    refreshConflictCount();
  }, [syncStatus, refreshConflictCount]);

  useEffect(() => {
    const interval = setInterval(refreshConflictCount, 10000);
    return () => clearInterval(interval);
  }, [refreshConflictCount]);

  const handleConflictResolved = () => {
    refreshConflictCount();
    if (isOnline) {
      triggerSync();
    }
  };

  // Rien si online, pas de changements en attente et pas de conflits
  if (isOnline && pendingChanges === 0 && syncStatus === 'idle' && conflictCount === 0) {
    return null;
  }

  // Déterminer le type de bulle à afficher
  const getBubbleStyle = () => {
    if (conflictCount > 0) return 'bg-amber-600 text-white';
    if (!isOnline) return 'bg-amber-500 text-white';
    if (syncStatus === 'syncing') return 'bg-primary-500 text-white';
    if (syncStatus === 'error') return 'bg-red-500 text-white';
    if (syncStatus === 'success') return 'bg-emerald-500 text-white';
    if (pendingChanges > 0) return 'bg-primary-500 text-white';
    return 'bg-gray-600 text-white';
  };

  const getBubbleIcon = () => {
    if (conflictCount > 0) return 'ri-alert-line';
    if (!isOnline) return 'ri-wifi-off-line';
    if (syncStatus === 'syncing') return 'ri-loader-4-line animate-spin';
    if (syncStatus === 'error') return 'ri-error-warning-line';
    if (syncStatus === 'success') return 'ri-check-line';
    if (pendingChanges > 0) return 'ri-refresh-line';
    return 'ri-cloud-off-line';
  };

  const getBubbleText = () => {
    if (conflictCount > 0) return `${conflictCount}`;
    if (!isOnline) return '';
    if (syncStatus === 'syncing') return '';
    if (syncStatus === 'error') return '';
    if (pendingChanges > 0) return `${pendingChanges}`;
    return '';
  };

  const getTooltipTitle = () => {
    if (conflictCount > 0) return `${conflictCount} conflit${conflictCount > 1 ? 's' : ''} de synchronisation`;
    if (!isOnline) return 'Mode hors-ligne';
    if (syncStatus === 'syncing') return 'Synchronisation en cours...';
    if (syncStatus === 'error') return 'Échec de synchronisation';
    if (syncStatus === 'success') return 'Synchronisation réussie';
    if (pendingChanges > 0) return `${pendingChanges} modification${pendingChanges > 1 ? 's' : ''} en attente`;
    return '';
  };

  const getTooltipDetail = () => {
    if (!isOnline) return 'Les modifications seront synchronisées au retour de la connexion';
    if (pendingChanges > 0 && syncStatus !== 'syncing') return 'Cliquez pour synchroniser maintenant';
    if (syncStatus === 'success' && lastSyncTime) {
      return `Dernière synchro : ${new Date(lastSyncTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (syncStatus === 'error') return 'Cliquez pour réessayer';
    return '';
  };

  const handleBubbleClick = () => {
    if (conflictCount > 0) {
      setShowConflictModal(true);
      return;
    }
    if (syncStatus === 'error' || (pendingChanges > 0 && syncStatus !== 'syncing')) {
      triggerSync();
      return;
    }
    // Toggle expanded view for other cases
    setExpanded(!expanded);
  };

  return (
    <>
      {/* Bulle flottante */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-2">
        {/* Panneau d'info étendu */}
        {expanded && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 mb-2 min-w-[260px] max-w-[320px] animate-fadeIn">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 ${getBubbleStyle().split(' ')[0]} rounded-full flex items-center justify-center flex-shrink-0`}>
                <i className={`${getBubbleIcon().replace(' animate-spin', '')} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900">{getTooltipTitle()}</p>
                {getTooltipDetail() && (
                  <p className="text-xs text-gray-500 mt-1">{getTooltipDetail()}</p>
                )}
                {conflictCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowConflictModal(true)}
                    className="mt-2 px-3 py-1.5 bg-amber-600 text-white rounded-full text-xs font-medium hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Résoudre
                  </button>
                )}
                {syncStatus === 'error' && (
                  <button
                    type="button"
                    onClick={triggerSync}
                    className="mt-2 px-3 py-1.5 bg-red-500 text-white rounded-full text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Réessayer
                  </button>
                )}
                {pendingChanges > 0 && syncStatus !== 'syncing' && syncStatus !== 'error' && (
                  <button
                    type="button"
                    onClick={triggerSync}
                    className="mt-2 px-3 py-1.5 bg-primary-500 text-white rounded-full text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Synchroniser
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center flex-shrink-0 cursor-pointer"
              >
                <i className="ri-close-line text-gray-400 text-sm"></i>
              </button>
            </div>
          </div>
        )}

        {/* Bulle principale */}
        <button
          type="button"
          onClick={handleBubbleClick}
          className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 cursor-pointer ${getBubbleStyle()}`}
          title={getTooltipTitle()}
        >
          {!isOnline && (
            <span className="absolute top-0.5 right-0.5 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </span>
          )}

          <i className={`${getBubbleIcon()} text-xl`}></i>

          {/* Badge compteur */}
          {getBubbleText() && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs font-bold flex items-center justify-center shadow">
              {getBubbleText()}
            </span>
          )}
        </button>
      </div>

      {/* Modal de résolution de conflits */}
      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onResolved={handleConflictResolved}
      />
    </>
  );
}